import { React, useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Avatar,
  Tabs,
  Tab,
  TableBody,
  TableCell,
  TableRow,
  Table,
  TableContainer,
  TableHead,
  Backdrop,
  CircularProgress,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GetAppIcon from "@mui/icons-material/GetApp";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import SearchIcon from "@mui/icons-material/Search";
import GavelIcon from "@mui/icons-material/Gavel";
import { getEstadisticasEquipoById } from "../services/equipoEstadisticasService";
import { getClasificacionesGrupoTemporada } from "../services/clasificacionService";
import { getJornadasByIdGrupoAndTemporada } from "../services/jornadasService";
import WeatherInfo from "../components/WeatherInfo";
import ReportGoalStatistics from "../components/reports/ReportGoalStatistics";
import ReportGlobalStatistics from "../components/reports/ReportGlobalStatistics";
import EquiposRadarComparacion from "../components/reports/ReportTeamComparison";
import RefereeReportProfile from "../components/reports/RefereeReportProfile";
import { ReportCoaching, ReportPlayerLeaders, ReportSquad } from "../components/reports/ReportPeople";
import ReportDiscipline from "../components/reports/ReportDiscipline";
import ReportPreviousDismissals from "../components/reports/ReportPreviousDismissals";
import { playerStatisticValue } from "../utils/playerStatistics";
import { getJugadorEstaditicasByIdGrupoAndIdTemporada } from "../services/jugadorEstadisticasService";
import { getActaByTemporadaAndCodActa } from "../services/actasService";
import { getMinutosPorGol } from "../helpers/golesHelper";
import { getJugadoresDestados } from "../helpers/golesHelper";
import { getInformeRival } from "../services/reportsServices";
import { getMyReportableCompetitions } from "../services/teamManagementService";

// Design System
import {
  Card,
  CardHeader as BaseCardHeader,
  CardContent,
  Button,
  Alert,
  Loading,
  EmptyState,
  Container,
  CSSGrid as Grid,
} from "../design-system";
import { colors } from "../design-system";
import usePageTitle from '../hooks/usePageTitle';
import usePageTour from '../hooks/usePageTour';
import { useAuth } from '../context/AuthContext';
import { LockedAction, UpgradeBoundary } from '../components/license/LicenseGate';
import { canGenerateMatchReport } from '../utils/reportPermissions';
import { buscarArbitros, getEstadisticasArbitro } from "../services/arbitroEstadisticasService";
import { aggregateRefereeStatistics } from "../helpers/arbitroEstadisticasHelper";
import { getMatchDraftKey, readReportDraft, writeReportDraft } from "../utils/matchReportDraft";
import { exportReportExample } from "../services/reportExamplesService";
import RivalReportOverview, { ReportSection, ReportSnapshot } from "../components/reports/RivalReportOverview";
import ReportAdvanced from "../components/reports/ReportAdvanced";
import { reportAdvancedPeriod } from "../utils/rivalAdvancedReport";
import { ReportResultCard, ReportHalftimeCard, ReportCleanSheets, ReportStreaks } from "../components/reports/ReportPerformance";
import { ReportMatchdays, ReportRecentForm, ReportContextStandings } from "../components/reports/ReportCompetitionContext";
import { reportGoalIntervals, reportNumber, reportRecentMatches, reportSum } from "../utils/rivalReport";
import { getMatchdayNumber } from "../utils/matchdays";
import { isGoalkeeper } from "../utils/playerDiscovery";
import "./InformePartidoPageModern.css";

const REPORT_SECTIONS = ['overview', 'performance', 'goals', 'players', 'discipline', 'comparison', 'advanced', 'all'];
const CardHeader = ({ icon, ...props }) => (
  <BaseCardHeader avatar={icon} titleTypographyProps={{ component: 'h2' }} {...props} />
);

export const parseReportMatchDate = (date, time) => {
  const iso = String(date || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const local = String(date || "").match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  const clock = String(time || "").match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if ((!iso && !local) || !clock) return null;
  const [year, month, day] = (iso ? [iso[1], iso[2], iso[3]] : [local[3], local[2], local[1]]).map(Number);
  const [hour, minute, second] = [clock[1], clock[2], clock[3] || 0].map(Number);
  const result = new Date(year, month - 1, day, hour, minute, second);
  return result.getFullYear() === year && result.getMonth() === month - 1
    && result.getDate() === day && result.getHours() === hour
    && result.getMinutes() === minute && result.getSeconds() === second ? result : null;
};

/**
 * InformePartidoPageModern - Modernized Pre-Match Report Page
 * The star feature of the app - professional match analysis
 */
const InformePartidoPageModern = ({ example = null } = {}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation('reports');
  usePageTitle(t('modern.title'));
  usePageTour(example ? null : 'informePartido');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const latestReportParams = useRef(searchParams);
  useEffect(() => { latestReportParams.current = searchParams; }, [searchParams]);
  const storedDraft = example ? null : readReportDraft();
  const partido = example?.partido || location.state?.partido || storedDraft?.partido;
  const initialDraft =
    storedDraft?.matchKey && storedDraft.matchKey === getMatchDraftKey(partido)
      ? storedDraft
      : {};
  const { hasPermission, profile } = useAuth();
  const isAdmin = profile?.system_role === 'admin';
  const canViewFullReport = Boolean(example) || hasPermission('reports.view');
  const canExportReports = Boolean(example) || hasPermission('reports.export');
  const sectionParam = searchParams.get('section');
  const activeSection = canViewFullReport && REPORT_SECTIONS.includes(sectionParam) ? sectionParam : 'overview';
  const advancedScope = ['total', 'local', 'visitante'].includes(searchParams.get('advancedScope'))
    ? searchParams.get('advancedScope') : 'match';
  const advancedPeriod = reportAdvancedPeriod(searchParams.get('advancedPeriod'));
  const changeAdvancedFilter = (key, value) => {
    const next = new URLSearchParams(latestReportParams.current);
    next.set(key, value);
    latestReportParams.current = next;
    setSearchParams(next, { preventScrollReset: true });
  };
  const changeSection = (section) => {
    const next = new URLSearchParams(searchParams);
    if (section === 'overview') next.delete('section');
    else next.set('section', section);
    setSearchParams(next, { preventScrollReset: true });
  };
  const ExportAction = example ? Button : LockedAction;
  const initialTeamLoader = useRef(null);
  const requestedTeam = example ? partido.equipoLocal : location.state?.equipoSeleccionado;
  useEffect(() => {
    if (requestedTeam && [partido?.equipoLocal, partido?.equipoVisitante].includes(requestedTeam)) {
      initialTeamLoader.current?.({ target: { value: requestedTeam } });
    }
  }, [requestedTeam, partido?.id, partido?.equipoLocal, partido?.equipoVisitante]);

  // State management
  const [equipoSeleccionado, setEquipoSeleccionado] = useState(initialDraft.equipoSeleccionado || "");
  const [estadisticasEquipo, setEstadisticasEquipo] = useState(initialDraft.estadisticasEquipo || null);
  const [clasificacionEquipo, setClasificacionEquipo] = useState(initialDraft.clasificacionEquipo || null);
  const [clasificacionContexto, setClasificacionContexto] = useState(initialDraft.clasificacionContexto || []);
  const [resultadoEquipo, setResultadoEquipo] = useState(initialDraft.resultadoEquipo || null);
  const [jornadas, setJornadas] = useState(initialDraft.jornadas || []);
  const [actaData, setActaData] = useState(null);
  const [ultimosPartidos, setUltimosPartidos] = useState(initialDraft.ultimosPartidos || []);
  const [esLocal, setEsLocal] = useState(initialDraft.esLocal ?? null);
  const [ultimosPartidosCasaOVisitante, setUltimosPartidosCasaOVisitante] = useState(initialDraft.ultimosPartidosCasaOVisitante || []);
  const [datosJugadores, setDatosJugadores] = useState(initialDraft.datosJugadores || []);
  const [goleadores, setGoleadores] = useState(initialDraft.goleadores || []);
  const [jugadoresMasMinutos, setJugadoresMasMinutos] = useState(initialDraft.jugadoresMasMinutos || []);
  const [jugadoresDestacados, setJugadoresDestacados] = useState(initialDraft.jugadoresDestacados || []);
  const [fortalezasJugadores] = useState(initialDraft.fortalezasJugadores || {});
  const [tarjetasEquipo, setTarjetasEquipo] = useState(initialDraft.tarjetasEquipo || null);
  const [arbitroQuery, setArbitroQuery] = useState("");
  const [arbitroResultados, setArbitroResultados] = useState([]);
  const [buscandoArbitro, setBuscandoArbitro] = useState(false);
  const [arbitroError, setArbitroError] = useState("");
  const [arbitroSeleccionado, setArbitroSeleccionado] = useState(initialDraft.arbitroSeleccionado || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [reportableCompetitions, setReportableCompetitions] = useState([]);
  const [loadingReportScopes, setLoadingReportScopes] = useState(true);

  // Rival team data (for radar comparison)
  const [estadisticasRival, setEstadisticasRival] = useState(initialDraft.estadisticasRival || null);
  const [clasificacionRival, setClasificacionRival] = useState(initialDraft.clasificacionRival || null);
  const [resultadoRival, setResultadoRival] = useState(initialDraft.resultadoRival || null);
  const [tarjetasRival, setTarjetasRival] = useState(initialDraft.tarjetasRival || null);

  // Sort state for tables
  const selectedTeamId = equipoSeleccionado === partido?.equipoLocal ? partido?.codEquipoLocal : partido?.codEquipoVisitante;
  const recentMatches = useMemo(() => reportRecentMatches(jornadas, selectedTeamId, equipoSeleccionado),
    [jornadas, selectedTeamId, equipoSeleccionado]);

  // Load acta on mount to get coaching staff
  const actaCode = partido?.codacta ?? partido?.codActa ?? partido?.idActa;
  useEffect(() => {
    if (!actaCode || !partido?.idTemporada) return;
    let cancelled = false;
    const load = async () => {
      try {
        const list = example ? example.actas : await getActaByTemporadaAndCodActa(partido.idTemporada, actaCode, navigate);
        if (!cancelled && list && list.length > 0) setActaData(list[0]);
      } catch (error) {
        console.error('No se pudo cargar el cuerpo técnico desde el acta:', error);
      }
    };
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actaCode, partido?.idTemporada]);

  useEffect(() => {
    if (example || !canExportReports || isAdmin) {
      setReportableCompetitions([]);
      setLoadingReportScopes(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingReportScopes(true);
    getMyReportableCompetitions()
      .then((competitions) => {
        if (!cancelled) setReportableCompetitions(competitions);
      })
      .catch(() => {
        if (!cancelled) setReportableCompetitions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingReportScopes(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canExportReports, isAdmin, example]);

  useEffect(() => {
    if (!partido || example) return;
    writeReportDraft({
      matchKey: getMatchDraftKey(partido),
      partido,
      equipoSeleccionado,
      estadisticasEquipo,
      clasificacionEquipo,
      clasificacionContexto,
      resultadoEquipo,
      jornadas,
      ultimosPartidos,
      esLocal,
      ultimosPartidosCasaOVisitante,
      datosJugadores,
      goleadores,
      jugadoresMasMinutos,
      jugadoresDestacados,
      fortalezasJugadores,
      tarjetasEquipo,
      estadisticasRival,
      clasificacionRival,
      resultadoRival,
      tarjetasRival,
      arbitroSeleccionado,
    });
  }, [
    example,
    partido,
    equipoSeleccionado,
    estadisticasEquipo,
    clasificacionEquipo,
    clasificacionContexto,
    resultadoEquipo,
    jornadas,
    ultimosPartidos,
    esLocal,
    ultimosPartidosCasaOVisitante,
    datosJugadores,
    goleadores,
    jugadoresMasMinutos,
    jugadoresDestacados,
    fortalezasJugadores,
    tarjetasEquipo,
    estadisticasRival,
    clasificacionRival,
    resultadoRival,
    tarjetasRival,
    arbitroSeleccionado,
  ]);

  const canGenerateReport = Boolean(example) || canGenerateMatchReport({
    isAdmin,
    partido,
    reportableCompetitions,
  });
  if (!partido) {
    return (
      <Container>
        <EmptyState
          type="error"
          title={t('errors.noMatch')}
          message={t('errors.noMatchDesc')}
          action={
            <Button variant="contained" onClick={() => navigate(-1)}>
              {t('header.back')}
            </Button>
          }
        />
      </Container>
    );
  }

  const fecha = parseReportMatchDate(partido.fecha, partido.hora);

  let fechaFormateada;
  if (fecha) {
    fechaFormateada = fecha.toLocaleString(i18n?.language, {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } else if (partido.fecha) {
    fechaFormateada = partido.fecha;
  } else {
    fechaFormateada = t('match.dateUnconfirmed');
  }

  const ciudad = partido.campo?.replace(/\(HA\)/g, "").trim();

  // Helper functions
  const obtenerClasificacionContexto = (clasificacion, nombreEquipo) => {
    const index = clasificacion.findIndex((e) => e.nombre === nombreEquipo);
    if (index === -1) return [];

    const total = clasificacion.length;
    const start = Math.max(0, index - 2);
    const end = Math.min(total, index + 3);

    let resultado = clasificacion.slice(start, end);

    if (resultado.length < 5 && end < total) {
      const faltan = 5 - resultado.length;
      resultado = clasificacion.slice(start, end + faltan);
    }

    if (resultado.length < 5 && start > 0) {
      const faltan = 5 - resultado.length;
      resultado = clasificacion.slice(Math.max(0, start - faltan), end);
    }

    return resultado;
  };

  const obtenerJugadores = async (idEquipo, idTemporada, idGrupo) => {
    const data = example ? example.jugadores : await getJugadorEstaditicasByIdGrupoAndIdTemporada(
      idGrupo,
      idTemporada,
      navigate
    );
    return data.filter((jugador) => String(jugador.idEquipo) === String(idEquipo));
  };

  const esLocalEquipo = (partido, equipo) => {
    return partido.equipoLocal === equipo;
  };

  // Card statistics helper
  const computeCardStats = (jugadores) => {
    const safeVal = (j, field) => playerStatisticValue(j, 'tarjetas', field);

    const perPlayer = jugadores.map((j) => {
      const amarillas =
        safeVal(j, 'tarjetasAmarillasGanandoLocal') + safeVal(j, 'tarjetasAmarillasPerdiendoLocal') +
        safeVal(j, 'tarjetasAmarillasEmpatandoLocal') + safeVal(j, 'tarjetasAmarillasGanandoVisitante') +
        safeVal(j, 'tarjetasAmarillasPerdiendoVisitante') + safeVal(j, 'tarjetasAmarillasEmpatandoVisitante');
      const segundas =
        safeVal(j, 'segundasAmarillasGanandoLocal') + safeVal(j, 'segundasAmarillasPerdiendoLocal') +
        safeVal(j, 'segundasAmarillasEmpatandoLocal') + safeVal(j, 'segundasAmarillasGanandoVisitante') +
        safeVal(j, 'segundasAmarillasPerdiendoVisitante') + safeVal(j, 'segundasAmarillasEmpatandoVisitante');
      const rojas =
        safeVal(j, 'tarjetasRojasGanandoLocal') + safeVal(j, 'tarjetasRojasPerdiendoLocal') +
        safeVal(j, 'tarjetasRojasEmpatandoLocal') + safeVal(j, 'tarjetasRojasGanandoVisitante') +
        safeVal(j, 'tarjetasRojasPerdiendoVisitante') + safeVal(j, 'tarjetasRojasEmpatandoVisitante');

      // By game state
      const ganando = safeVal(j, 'tarjetasAmarillasGanandoLocal') + safeVal(j, 'tarjetasAmarillasGanandoVisitante') +
        safeVal(j, 'segundasAmarillasGanandoLocal') + safeVal(j, 'segundasAmarillasGanandoVisitante') +
        safeVal(j, 'tarjetasRojasGanandoLocal') + safeVal(j, 'tarjetasRojasGanandoVisitante');
      const empatando = safeVal(j, 'tarjetasAmarillasEmpatandoLocal') + safeVal(j, 'tarjetasAmarillasEmpatandoVisitante') +
        safeVal(j, 'segundasAmarillasEmpatandoLocal') + safeVal(j, 'segundasAmarillasEmpatandoVisitante') +
        safeVal(j, 'tarjetasRojasEmpatandoLocal') + safeVal(j, 'tarjetasRojasEmpatandoVisitante');
      const perdiendo = safeVal(j, 'tarjetasAmarillasPerdiendoLocal') + safeVal(j, 'tarjetasAmarillasPerdiendoVisitante') +
        safeVal(j, 'segundasAmarillasPerdiendoLocal') + safeVal(j, 'segundasAmarillasPerdiendoVisitante') +
        safeVal(j, 'tarjetasRojasPerdiendoLocal') + safeVal(j, 'tarjetasRojasPerdiendoVisitante');

      // By location
      const local = safeVal(j, 'tarjetasAmarillasGanandoLocal') + safeVal(j, 'tarjetasAmarillasPerdiendoLocal') + safeVal(j, 'tarjetasAmarillasEmpatandoLocal') +
        safeVal(j, 'segundasAmarillasGanandoLocal') + safeVal(j, 'segundasAmarillasPerdiendoLocal') + safeVal(j, 'segundasAmarillasEmpatandoLocal') +
        safeVal(j, 'tarjetasRojasGanandoLocal') + safeVal(j, 'tarjetasRojasPerdiendoLocal') + safeVal(j, 'tarjetasRojasEmpatandoLocal');
      const visitante = safeVal(j, 'tarjetasAmarillasGanandoVisitante') + safeVal(j, 'tarjetasAmarillasPerdiendoVisitante') + safeVal(j, 'tarjetasAmarillasEmpatandoVisitante') +
        safeVal(j, 'segundasAmarillasGanandoVisitante') + safeVal(j, 'segundasAmarillasPerdiendoVisitante') + safeVal(j, 'segundasAmarillasEmpatandoVisitante') +
        safeVal(j, 'tarjetasRojasGanandoVisitante') + safeVal(j, 'tarjetasRojasPerdiendoVisitante') + safeVal(j, 'tarjetasRojasEmpatandoVisitante');

      return {
        id: j.id, nombre: j.nombre, dorsalMasUsado: j.dorsalMasUsado,
        esPortero: isGoalkeeper(j),
        amarillas, segundas, rojas, total: amarillas + segundas + rojas,
        ganando, empatando, perdiendo, local, visitante,
      };
    });

    const jugadoresConTarjetas = perPlayer.filter((p) => p.total > 0).sort((a, b) => b.total - a.total);
    const sum = (arr, key) => arr.reduce((acc, p) => acc + p[key], 0);

    // Per-type breakdown by location (use original jugadores, not perPlayer)
    const amarillasLocal = jugadores.reduce((acc, j) =>
      acc + safeVal(j, 'tarjetasAmarillasGanandoLocal') + safeVal(j, 'tarjetasAmarillasPerdiendoLocal') + safeVal(j, 'tarjetasAmarillasEmpatandoLocal'), 0);
    const amarillasVisitante = jugadores.reduce((acc, j) =>
      acc + safeVal(j, 'tarjetasAmarillasGanandoVisitante') + safeVal(j, 'tarjetasAmarillasPerdiendoVisitante') + safeVal(j, 'tarjetasAmarillasEmpatandoVisitante'), 0);
    const rojasLocal = jugadores.reduce((acc, j) =>
      acc + safeVal(j, 'tarjetasRojasGanandoLocal') + safeVal(j, 'tarjetasRojasPerdiendoLocal') + safeVal(j, 'tarjetasRojasEmpatandoLocal'), 0);
    const rojasVisitante = jugadores.reduce((acc, j) =>
      acc + safeVal(j, 'tarjetasRojasGanandoVisitante') + safeVal(j, 'tarjetasRojasPerdiendoVisitante') + safeVal(j, 'tarjetasRojasEmpatandoVisitante'), 0);

    return {
      sourceAvailable: jugadores.some(jugador =>
        ['tarjetasAmarillas', 'segundasAmarillas', 'tarjetasRojas'].some(prefix =>
          ['Ganando', 'Empatando', 'Perdiendo'].some(state =>
            ['Local', 'Visitante'].some(side =>
              reportNumber(jugador.tarjetas?.[`${prefix}${state}${side}`] ?? jugador[`${prefix}${state}${side}`]) !== null)))),
      jugadores: jugadoresConTarjetas,
      totalAmarillas: sum(perPlayer, 'amarillas'),
      totalSegundas: sum(perPlayer, 'segundas'),
      totalRojas: sum(perPlayer, 'rojas'),
      ganando: sum(perPlayer, 'ganando'),
      empatando: sum(perPlayer, 'empatando'),
      perdiendo: sum(perPlayer, 'perdiendo'),
      local: sum(perPlayer, 'local'),
      visitante: sum(perPlayer, 'visitante'),
      amarillasLocal,
      amarillasVisitante,
      rojasLocal,
      rojasVisitante,
      segundasLocal: jugadores.reduce((total, jugador) => total +
        ['Ganando', 'Empatando', 'Perdiendo'].reduce((sum, state) =>
          sum + safeVal(jugador, `segundasAmarillas${state}Local`), 0), 0),
      segundasVisitante: jugadores.reduce((total, jugador) => total +
        ['Ganando', 'Empatando', 'Perdiendo'].reduce((sum, state) =>
          sum + safeVal(jugador, `segundasAmarillas${state}Visitante`), 0), 0),
    };
  };

  const getAllData = async (idTemporada, idCompeticion, idGrupo, idEstadisticasEquipo, equipo) => {
    try {
      setIsLoadingData(true);
      setError(null);

      // Get team statistics
      const estadisticas = example ? example.equipos.find(item => String(item.idEquipo) === String(idEstadisticasEquipo)) : await getEstadisticasEquipoById(
        idTemporada,
        `${idEstadisticasEquipo}_${idCompeticion}_${idGrupo}_${idTemporada}`
      );
      setEstadisticasEquipo(estadisticas);

      // Get classification
      const snapshots = example ? example.clasificaciones : await getClasificacionesGrupoTemporada(
        idTemporada,
        idGrupo,
        navigate
      );

      const dataClasificacion = [...(snapshots || [])].sort((a, b) =>
        (getMatchdayNumber(a) ?? -1) - (getMatchdayNumber(b) ?? -1));
      if (dataClasificacion.length === 0) {
        setClasificacionEquipo(null);
        setClasificacionContexto([]);
      } else {
        const equipoEncontrado = dataClasificacion[dataClasificacion.length - 1]?.clasificacion?.find(
          (equipoClasificacion) => String(equipoClasificacion.codequipo) === String(idEstadisticasEquipo)
        );
        setClasificacionEquipo(equipoEncontrado || null);

        const contextoClasificacion = obtenerClasificacionContexto(
          dataClasificacion[dataClasificacion.length - 1]?.clasificacion || [],
          equipoEncontrado?.nombre
        );
        setClasificacionContexto(contextoClasificacion);
      }

      if (!canViewFullReport) {
        const equipoEncontrado = dataClasificacion?.[dataClasificacion.length - 1]?.clasificacion?.find(
          (equipoClasificacion) => String(equipoClasificacion.codequipo) === String(idEstadisticasEquipo)
        );
        const datosEquipo = {
          general: {
            jugados: estadisticas.partidos.partidosJugados,
            ganados: equipoEncontrado?.ganados || 0,
            empatados: equipoEncontrado?.empatados || 0,
            perdidos: equipoEncontrado?.perdidos || 0,
            golesFavor: equipoEncontrado?.golesFavor || 0,
            golesContra: equipoEncontrado?.golesContra || 0,
          },
          casa: {},
          fuera: {},
        };
        setResultadoEquipo(datosEquipo);
        return datosEquipo;
      }

      // Get matchdays and matches
      const jornadasEquipo = example ? { jornadas: example.jornadas } : await getJornadasByIdGrupoAndTemporada(idGrupo, idTemporada, navigate);
      setJornadas(jornadasEquipo.jornadas);

      const partidosOrdenados = reportRecentMatches(jornadasEquipo.jornadas, idEstadisticasEquipo, equipo, Number.MAX_SAFE_INTEGER)
        .map(match => ({
          ...match, jornada: match.round,
          golesCasa: Number(match.golesCasa), golesVisitante: Number(match.golesVisitante),
          escudoLocal: match.escudoEquipoLocal, escudoVisitante: match.escudoEquipoVisitante,
        }));

      const ultimosPartidos = partidosOrdenados
        .filter((partido) => !isNaN(partido.golesCasa) && !isNaN(partido.golesVisitante))
        .slice(-5);

      let ultimosPartidosCasaOVisitante = [];
      if (esLocalEquipo(partido, equipo)) {
        ultimosPartidosCasaOVisitante = partidosOrdenados
          .filter((p) => p.home)
          .slice(-5);
      } else {
        ultimosPartidosCasaOVisitante = partidosOrdenados
          .filter((p) => !p.home)
          .slice(-5);
      }

      setUltimosPartidos(ultimosPartidos);
      setUltimosPartidosCasaOVisitante(ultimosPartidosCasaOVisitante);

      // Get players
      const jugadores = await obtenerJugadores(idEstadisticasEquipo, idTemporada, idGrupo);
      setDatosJugadores(jugadores);

      // Top scorers
      const goleadores = jugadores
        .filter((jugador) => (jugador.goles?.golesTotales ?? 0) + (jugador.goles?.golesTotalesPenalti ?? 0) > 0)
        .sort((a, b) => {
          const golesA = (a.goles?.golesTotales ?? 0) + (a.goles?.golesTotalesPenalti ?? 0);
          const golesB = (b.goles?.golesTotales ?? 0) + (b.goles?.golesTotalesPenalti ?? 0);
          return golesB - golesA;
        })
      setGoleadores(goleadores);

      // Most minutes played
      const jugadoresMasMinutos = jugadores
        .filter((jugador) => (jugador.minutos?.minutosJugadosLocal ?? 0) + (jugador.minutos?.minutosJugadosVisitante ?? 0) > 0)
        .sort((a, b) => {
          const minutosA = (a.minutos?.minutosJugadosLocal ?? 0) + (a.minutos?.minutosJugadosVisitante ?? 0);
          const minutosB = (b.minutos?.minutosJugadosLocal ?? 0) + (b.minutos?.minutosJugadosVisitante ?? 0);
          return minutosB - minutosA;
        })
        .slice(0, 8);
      setJugadoresMasMinutos(jugadoresMasMinutos);

      const jugadoresDestacados = getJugadoresDestados(goleadores, jugadoresMasMinutos);
      setJugadoresDestacados(jugadoresDestacados);

      // Card statistics
      const tarjetas = computeCardStats(jugadores);
      setTarjetasEquipo(tarjetas);

      const equipoEncontrado = dataClasificacion?.[dataClasificacion.length - 1]?.clasificacion?.find(
        (equipoClasificacion) => String(equipoClasificacion.codequipo) === String(idEstadisticasEquipo)
      );

      const goalIntervals = reportGoalIntervals(estadisticas, equipoEncontrado);
      const datosEquipo = {
        general: {
          jugados: estadisticas.partidos.partidosJugados,
          ganados: equipoEncontrado?.ganados || 0,
          empatados: equipoEncontrado?.empatados || 0,
          perdidos: equipoEncontrado?.perdidos || 0,
          golesFavor: equipoEncontrado?.golesFavor || 0,
          golesContra: equipoEncontrado?.golesContra || 0,
        },
        casa: {
          jugados: estadisticas.partidos.partidosJugadosLocal,
          ganados: equipoEncontrado?.ganadosCasa || 0,
          empatados: equipoEncontrado?.empatadosCasa || 0,
          perdidos: equipoEncontrado?.perdidosCasa || 0,
          golesFavor: reportSum(...goalIntervals.map(row => row.homeFor)),
          golesContra: reportSum(...goalIntervals.map(row => row.homeAgainst)),
        },
        fuera: {
          jugados: estadisticas.partidos.partidosJugadosVisitante,
          ganados: equipoEncontrado?.ganadosFuera || 0,
          empatados: equipoEncontrado?.empatadosFuera || 0,
          perdidos: equipoEncontrado?.perdidosFuera || 0,
          golesFavor: reportSum(...goalIntervals.map(row => row.awayFor)),
          golesContra: reportSum(...goalIntervals.map(row => row.awayAgainst)),
        },
      };

      setResultadoEquipo(datosEquipo);
      return datosEquipo;
    } catch (error) {
      setError(t('errors.loadData'));
      return null;
    }
  };

  // Comparison failures leave the selected team's report available.
  const getRivalData = async (idTemporada, idCompeticion, idGrupo, idEquipo) => {
    try {
      const estadisticas = example ? example.equipos.find(item => String(item.idEquipo) === String(idEquipo)) : await getEstadisticasEquipoById(
        idTemporada,
        `${idEquipo}_${idCompeticion}_${idGrupo}_${idTemporada}`
      );
      setEstadisticasRival(estadisticas);

      const snapshots = example ? example.clasificaciones : await getClasificacionesGrupoTemporada(
        idTemporada, idGrupo, navigate
      );
      const dataClasificacion = [...(snapshots || [])].sort((a, b) =>
        (getMatchdayNumber(a) ?? -1) - (getMatchdayNumber(b) ?? -1));

      let clasificacion = null;
      if (dataClasificacion?.length > 0) {
        clasificacion =
          dataClasificacion[dataClasificacion.length - 1]?.clasificacion?.find(
            (e) => String(e.codequipo) === String(idEquipo)
          ) || null;
      }
      setClasificacionRival(clasificacion);

      const jugadores = await obtenerJugadores(idEquipo, idTemporada, idGrupo);
      const tarjetas = computeCardStats(jugadores);
      setTarjetasRival(tarjetas);

      const goalIntervals = reportGoalIntervals(estadisticas, clasificacion);
      const resultado = {
        general: {
          jugados: estadisticas?.partidos?.partidosJugados || 0,
          ganados: clasificacion?.ganados || 0,
          empatados: clasificacion?.empatados || 0,
          perdidos: clasificacion?.perdidos || 0,
          golesFavor: clasificacion?.golesFavor || 0,
          golesContra: clasificacion?.golesContra || 0,
        },
        casa: {
          jugados: estadisticas?.partidos?.partidosJugadosLocal || 0,
          ganados: clasificacion?.ganadosCasa || 0,
          empatados: clasificacion?.empatadosCasa || 0,
          perdidos: clasificacion?.perdidosCasa || 0,
          golesFavor: reportSum(...goalIntervals.map(row => row.homeFor)),
          golesContra: reportSum(...goalIntervals.map(row => row.homeAgainst)),
        },
        fuera: {
          jugados: estadisticas?.partidos?.partidosJugadosVisitante || 0,
          ganados: clasificacion?.ganadosFuera || 0,
          empatados: clasificacion?.empatadosFuera || 0,
          perdidos: clasificacion?.perdidosFuera || 0,
          golesFavor: reportSum(...goalIntervals.map(row => row.awayFor)),
          golesContra: reportSum(...goalIntervals.map(row => row.awayAgainst)),
        },
      };
      setResultadoRival(resultado);
    } catch (_) {
      // non-blocking — comparison section simply won't render
    }
  };

  const handleChangeEquipo = async (event) => {
    const equipo = event.target.value;
    if (isLoadingData) return;
    setEquipoSeleccionado(equipo);
    setEsLocal(partido.equipoLocal === equipo);
    setResultadoEquipo(null);
    setEstadisticasEquipo(null);
    setClasificacionEquipo(null);

    const idEquipo = equipo === partido.equipoLocal ? partido.codEquipoLocal : partido.codEquipoVisitante;
    const idRival = equipo === partido.equipoLocal ? partido.codEquipoVisitante : partido.codEquipoLocal;

    // Reset rival data so stale comparison doesn't show
    setEstadisticasRival(null);
    setClasificacionRival(null);
    setResultadoRival(null);
    setTarjetasRival(null);

    const requests = [
      getAllData(partido.idTemporada, partido.idCompeticion, partido.idGrupo, idEquipo, equipo),
    ];
    if (canViewFullReport) {
      requests.push(
        getRivalData(partido.idTemporada, partido.idCompeticion, partido.idGrupo, idRival)
      );
    }
    try {
      await Promise.all(requests);
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleBuscarArbitro = async (event) => {
    event.preventDefault();
    const query = arbitroQuery.trim();
    if (query.length < 2) {
      setArbitroError(t('referee.minimum'));
      return;
    }
    setBuscandoArbitro(true);
    setArbitroError("");
    try {
      const resultados = await buscarArbitros(query, navigate);
      setArbitroResultados(Array.isArray(resultados) ? resultados : []);
    } catch {
      setArbitroResultados([]);
      setArbitroError(t('referee.searchError'));
    } finally {
      setBuscandoArbitro(false);
    }
  };

  const handleSeleccionarArbitro = async (arbitro) => {
    setBuscandoArbitro(true);
    setArbitroError("");
    try {
      const documentos = await getEstadisticasArbitro(
        arbitro.idArbitro,
        navigate
      );
      setArbitroSeleccionado(
        aggregateRefereeStatistics(Array.isArray(documentos) ? documentos : [])
      );
      setArbitroResultados([]);
      setArbitroQuery(arbitro.nombre || "");
    } catch {
      setArbitroError(t('referee.detailError'));
    } finally {
      setBuscandoArbitro(false);
    }
  };

  initialTeamLoader.current = handleChangeEquipo;

  const prepararDatosYGenerarInforme = async () => {
    setLoading(true);
    setError(null);
    try {
      const datosInforme = {
        idTemporada: partido.idTemporada,
        idCompeticion: partido.idCompeticion,
        idGrupo: partido.idGrupo,
        idEstadisticasEquipo: `${esLocalEquipo(partido, equipoSeleccionado) ? partido.codEquipoLocal : partido.codEquipoVisitante}_${partido.idCompeticion}_${partido.idGrupo}_${partido.idTemporada}`,
        equipo: equipoSeleccionado,
        idPartido: partido.id,
        idJornada: partido.idJornada,
        jugadoresDestacados: jugadoresDestacados.map((jugador) => {
          const key = String(jugador.idJugador ?? jugador.id ?? jugador.nombre);
          return {
            idJugador: jugador.idJugador ?? jugador.id ?? null,
            nombre: jugador.nombre,
            dorsalMasUsado: jugador.dorsalMasUsado,
            goles:
              (jugador.goles?.golesTotales ?? 0) +
              (jugador.goles?.golesTotalesPenalti ?? 0),
            minutos:
              (jugador.minutos?.minutosJugadosLocal ?? 0) +
              (jugador.minutos?.minutosJugadosVisitante ?? 0),
            partidos:
              (jugador.partidos?.partidosJugadosLocal ?? 0) +
              (jugador.partidos?.partidosJugadosVisitante ?? 0),
            titularidades:
              (jugador.partidos?.titularidadesLocal ?? 0) +
              (jugador.partidos?.titularidadesVisitante ?? 0),
            minutosPorGol: getMinutosPorGol(jugador),
            fortalezas: fortalezasJugadores[key] || "",
          };
        }),
        arbitro: arbitroSeleccionado,
        edition: "modern",
        advancedScope,
        advancedPeriod,
      };

      if (example) {
        await exportReportExample(example.slug, esLocalEquipo(partido, equipoSeleccionado) ? "home" : "away");
      } else {
        await getInformeRival(datosInforme, navigate);
      }
    } catch (err) {
      setError(t('errors.generateReport'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      className="rr-report"
      data-theme={theme.palette.mode}
      lang={i18n?.resolvedLanguage || i18n?.language}
    >
      <header className="rr-heading" data-tour="report-header">
        <div>
          <span className="rr-eyebrow">{t('modern.eyebrow')}</span>
          <h1>{t('modern.title')}</h1>
          <p>{t('modern.subtitle')}</p>
        </div>
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={() => example ? navigate('/#ejemplo') : navigate(-1)}
            >
              {t('header.back')}
            </Button>
      </header>

      <div className="rr-body">
        <section className="rr-fixture" data-tour="report-match-card" aria-label={t('modern.fixture')}>
          <div className="rr-fixture-context">
            <span className="rr-eyebrow">{t('match.matchday', { n: partido.jornada ?? '—' })}</span>
            <p>{fechaFormateada}</p><p>{ciudad || t('match.venueUnconfirmed')}</p>
            {ciudad && !example && (
              <Box sx={{ mt: 1 }}>
                <WeatherInfo ciudad={ciudad} fecha={partido.fecha} hora={partido.hora} />
              </Box>
            )}
          </div>
          <div className="rr-team-choice" data-tour="report-team-selection" role="group" aria-label={t('teamSelection.label')} aria-busy={isLoadingData}>
            {[
              [partido.equipoLocal, partido.escudoEquipoLocal, 'match.home'],
              [partido.equipoVisitante, partido.escudoEquipoVisitante, 'match.away'],
            ].map(([name, crest, side]) => <button key={side} type="button" aria-pressed={equipoSeleccionado === name}
              disabled={isLoadingData} onClick={() => handleChangeEquipo({ target: { value: name } })}>
              <Avatar src={crest} alt="" variant="rounded">{name?.slice(0, 1)}</Avatar>
              <span><small>{t(side)}</small><strong>{name}</strong>
                <small>{equipoSeleccionado === name ? t('modern.selectedTeam') : t('modern.analyzeTeam')}</small></span>
            </button>)}
          </div>
        </section>
        <div className="rr-toolbar">
          <p>{t(equipoSeleccionado ? 'modern.sourceNote' : 'teamSelection.hint')}</p>
          {equipoSeleccionado && (
            <ExportAction
              {...(!example ? { permission: "reports.export" } : {})}
              data-tour="report-export"
              variant="contained"
              startIcon={<GetAppIcon />}
              onClick={() => prepararDatosYGenerarInforme()}
              disabled={loading || isLoadingData || !resultadoEquipo || (!isAdmin && (loadingReportScopes || !canGenerateReport))}
            >
              {t('header.export')}
            </ExportAction>
          )}
        </div>
            {!loadingReportScopes && canExportReports && !canGenerateReport && (
              <Alert variant="warning" sx={{ mb: 2 }}>
                {t('modern.exportScope')}
              </Alert>
            )}

        {/* Loading State */}
        {isLoadingData && (
          <Box sx={{ py: 8 }}>
            <Loading variant="soccer" size="large" text={t('loading.teamData')} />
          </Box>
        )}

        {/* Error State */}
        {error && !isLoadingData && (
          <Alert variant="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Team Analysis Content */}
        {equipoSeleccionado && resultadoEquipo && !isLoadingData && (
          <Box data-tour="report-analysis-content">
            <ReportSnapshot statistics={estadisticasEquipo} standing={clasificacionEquipo} name={equipoSeleccionado} />

            {canViewFullReport ? (
              <>
            <Tabs className="rr-tabs" value={activeSection} onChange={(_, section) => changeSection(section)}
              variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label={t('modern.navigation')}>
              {REPORT_SECTIONS.map(section => <Tab key={section} value={section} id={`rr-tab-${section}`}
                aria-controls={`rr-panel-${section}`} label={t(`modern.tabs.${section}`)} />)}
            </Tabs>
            <div className="rr-tab-description"><p>{t(`modern.descriptions.${activeSection}`)}</p>
              <span>{t('modern.detailPreserved')}</span></div>
            <div role="tabpanel" id={`rr-panel-${activeSection}`} aria-labelledby={`rr-tab-${activeSection}`} tabIndex={0}>
            <ReportSection active={activeSection} section="overview">
              <ReportAdvanced summary statistics={estadisticasEquipo} otherStatistics={estadisticasRival}
                match={partido} home={esLocal} name={equipoSeleccionado}
                otherName={esLocal ? partido.equipoVisitante : partido.equipoLocal}
                scope={advancedScope} period={advancedPeriod} onSection={changeSection} />
              <RivalReportOverview statistics={estadisticasEquipo} standing={clasificacionEquipo}
                otherStatistics={estadisticasRival} otherStanding={clasificacionRival}
                name={equipoSeleccionado} otherName={esLocal ? partido.equipoVisitante : partido.equipoLocal}
                recent={recentMatches} players={datosJugadores} home={esLocal} onSection={changeSection} />
            </ReportSection>
            <ReportSection active={activeSection} section="advanced">
              <ReportAdvanced statistics={estadisticasEquipo} otherStatistics={estadisticasRival}
                match={partido} home={esLocal} name={equipoSeleccionado} players={datosJugadores}
                otherName={esLocal ? partido.equipoVisitante : partido.equipoLocal}
                scope={advancedScope} period={advancedPeriod} onFilter={changeAdvancedFilter} />
            </ReportSection>
            <ReportSection active={activeSection} section="performance">
            <ReportMatchdays current={partido.jornada} rounds={jornadas} />

            </ReportSection>
            <ReportSection active={activeSection} section="players">
              <ReportCoaching staff={estadisticasEquipo?.cuerpoTecnico}
                matchCoach={actaData ? (esLocalEquipo(partido, equipoSeleccionado) ? actaData.entrenadorLocal : actaData.entrenadorVisitante) : null}
                team={equipoSeleccionado} players={datosJugadores} />
            </ReportSection>
            <ReportSection active={activeSection} section="performance">
            <div className="rr-recent-grid">
              <ReportRecentForm title={t('sections.recentMatches')} matches={ultimosPartidos} />
              <ReportRecentForm title={esLocal ? t('sections.recentMatchesHome') : t('sections.recentMatchesAway')}
                matches={ultimosPartidosCasaOVisitante} />
            </div>
            <ReportContextStandings teams={clasificacionContexto} selected={equipoSeleccionado} />

            {/* Team Stats */}
            <Grid className="rr-detail-grid" columns={2} gap={3} sx={{ mb: 3 }}>
              <ReportResultCard title={t('sections.generalStats')} stats={resultadoEquipo.general} />
              <ReportResultCard title={esLocal ? t('sections.homeStats') : t('sections.awayStats')}
                stats={esLocal ? resultadoEquipo.casa : resultadoEquipo.fuera} venue={esLocal ? 'home' : 'away'} />
            </Grid>

            {estadisticasEquipo?.partidos && <ReportHalftimeCard matches={estadisticasEquipo.partidos} />}

            <ReportCleanSheets matches={estadisticasEquipo?.partidos} />
            <ReportStreaks standing={clasificacionEquipo} />

            </ReportSection>
            <ReportSection active={activeSection} section="goals">
            <ReportGoalStatistics estadisticas={estadisticasEquipo} clasificacion={clasificacionEquipo} />

            </ReportSection>
            <ReportSection active={activeSection} section="performance">
            {estadisticasEquipo?.datos_extra && (
              <ReportGlobalStatistics estadisticas={estadisticasEquipo} clasificacion={clasificacionEquipo} missingValue="0" />
            )}

            </ReportSection>
            <ReportSection active={activeSection} section="players">
              <ReportPlayerLeaders scorers={goleadores} minutes={jugadoresMasMinutos} />
            </ReportSection>
            <ReportSection active={activeSection} section="goals">
            {/* Goles como Titular vs Suplente */}
            {datosJugadores.length > 0 && (() => {
              const golesData = datosJugadores
                .map((j) => {
                  const golesTitular = (j.partidos?.golesTitularLocal ?? 0) + (j.partidos?.golesTitularVisitante ?? 0);
                  const golesSuplente = (j.partidos?.golesSuplenteLocal ?? 0) + (j.partidos?.golesSuplenteVisitante ?? 0);
                  return { nombre: j.nombre, dorsal: j.dorsalMasUsado, golesTitular, golesSuplente, total: golesTitular + golesSuplente };
                })
                .filter((j) => j.total > 0)
                .sort((a, b) => b.total - a.total);
              if (golesData.length === 0) return null;
              const totales = golesData.reduce(
                (acc, j) => ({ golesTitular: acc.golesTitular + j.golesTitular, golesSuplente: acc.golesSuplente + j.golesSuplente, total: acc.total + j.total }),
                { golesTitular: 0, golesSuplente: 0, total: 0 }
              );
              return (
                <Card variant="elevated" sx={{ mb: 3 }}>
                  <CardHeader
                    title={t('sections.goalsByRole')}
                    icon={<SportsSoccerIcon />}
                  />
                  <CardContent>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>{t('table.number')}</strong></TableCell>
                            <TableCell><strong>{t('table.player')}</strong></TableCell>
                            <TableCell align="center"><strong>{t('table.starter')}</strong></TableCell>
                            <TableCell align="center"><strong>{t('table.substitute')}</strong></TableCell>
                            <TableCell align="center"><strong>{t('stats.total')}</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {golesData.map((j) => (
                            <TableRow key={j.nombre}>
                              <TableCell>{j.dorsal}</TableCell>
                              <TableCell>{j.nombre}</TableCell>
                              <TableCell align="center">{j.golesTitular}</TableCell>
                              <TableCell align="center">{j.golesSuplente}</TableCell>
                              <TableCell align="center"><strong>{j.total}</strong></TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ backgroundColor: 'action.hover' }}>
                            <TableCell colSpan={2}><strong>{t('table.totalTeam')}</strong></TableCell>
                            <TableCell align="center"><strong>{totales.golesTitular}</strong></TableCell>
                            <TableCell align="center"><strong>{totales.golesSuplente}</strong></TableCell>
                            <TableCell align="center"><strong>{totales.total}</strong></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              );
            })()}

            </ReportSection>
            <ReportSection active={activeSection} section="discipline">
              {activeSection !== 'all' && <ReportPreviousDismissals calendar={jornadas} teamId={selectedTeamId}
                teamName={equipoSeleccionado} match={partido} example={example} />}
              <ReportDiscipline cards={tarjetasEquipo} matches={estadisticasEquipo?.partidos} />
            </ReportSection>
            <ReportSection active={activeSection} section="players">
              <ReportPreviousDismissals calendar={jornadas} teamId={selectedTeamId}
                teamName={equipoSeleccionado} match={partido} example={example} />
              <ReportSquad players={datosJugadores} keyPlayers={jugadoresDestacados}
                profileContext={example ? undefined : {
                  season: partido.idTemporada, directoryFrom: location.pathname + location.search,
                }} />
            </ReportSection>
            <ReportSection active={activeSection} section="comparison">
            {!resultadoRival && <p className="rr-empty" role="status">{t('modern.comparisonUnavailable')}</p>}
            {/* Team Radar Comparison */}
            {resultadoRival && (
              <Box sx={{ mt: 3 }}>
                {(() => {
                 const esLocalSelected = equipoSeleccionado === partido.equipoLocal;
                 const datosLocal = esLocalSelected
                   ? {
                       resultado: resultadoEquipo,
                       estadisticas: estadisticasEquipo,
                       tarjetas: tarjetasEquipo,
                       clasificacion: clasificacionEquipo,
                     }
                   : {
                       resultado: resultadoRival,
                       estadisticas: estadisticasRival,
                       tarjetas: tarjetasRival,
                       clasificacion: clasificacionRival,
                     };
                 const datosVisitante = esLocalSelected
                   ? {
                       resultado: resultadoRival,
                       estadisticas: estadisticasRival,
                       tarjetas: tarjetasRival,
                       clasificacion: clasificacionRival,
                     }
                   : {
                       resultado: resultadoEquipo,
                       estadisticas: estadisticasEquipo,
                       tarjetas: tarjetasEquipo,
                       clasificacion: clasificacionEquipo,
                     };
                 return (
                   <EquiposRadarComparacion
                     equipoA={{ nombre: partido.equipoLocal, escudo: partido.escudoEquipoLocal }}
                     equipoB={{ nombre: partido.equipoVisitante, escudo: partido.escudoEquipoVisitante }}
                     datosA={datosLocal}
                     datosB={datosVisitante}
                   />
                 );
                })()}
              </Box>
            )}
            </ReportSection>
            </div>
              </>
            ) : (
              <UpgradeBoundary
                permission="reports.view"
                promptOnMount
                leaveOnClose
              />
            )}
          </Box>
        )}

        {equipoSeleccionado && !example && (!canViewFullReport || activeSection === 'discipline' || activeSection === 'all') && (
          <Card className="rr-referee-search" variant="elevated" sx={{ mt: 3 }}>
            <CardHeader title={t('referee.title')} icon={<GavelIcon />} />
            <CardContent>
              <Box component="form" onSubmit={handleBuscarArbitro}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    fullWidth
                    label={t('referee.searchLabel')}
                    placeholder={t('referee.searchPlaceholder')}
                    value={arbitroQuery}
                    onChange={(event) => setArbitroQuery(event.target.value)}
                    error={Boolean(arbitroError)}
                    helperText={arbitroError || t('referee.helper')}
                    inputProps={{ maxLength: 80 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon aria-hidden="true" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    type="submit"
                    variant="outlined"
                    disabled={buscandoArbitro}
                    startIcon={
                      buscandoArbitro
                        ? <CircularProgress size={18} color="inherit" />
                        : <SearchIcon />
                    }
                    sx={{ minHeight: 48, minWidth: { sm: 140 } }}
                  >
                    {t('referee.searchAction')}
                  </Button>
                </Stack>
              </Box>

              {arbitroResultados.length > 0 && (
                <List
                  aria-label={t('referee.results')}
                  sx={{ mt: 2, border: 1, borderColor: "divider", borderRadius: 2, p: 0 }}
                >
                  {arbitroResultados.map((arbitro, index) => (
                    <Box key={arbitro.idArbitro}>
                      {index > 0 && <Divider />}
                      <ListItemButton
                        onClick={() => handleSeleccionarArbitro(arbitro)}
                        sx={{ minHeight: 44 }}
                      >
                        <ListItemText
                          primary={arbitro.nombre}
                          secondary={t('referee.resultContext', {
                            seasons: arbitro.temporadas?.length || 0,
                            competitions: arbitro.competiciones || 0,
                          })}
                        />
                      </ListItemButton>
                    </Box>
                  ))}
                </List>
              )}

              {arbitroSeleccionado && (
                <Box sx={{ mt: 3 }}>
                  <RefereeReportProfile summary={arbitroSeleccionado} />
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Loading Backdrop for PPTX Export */}
      <Backdrop
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          color: colors.neutral.white,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: "column",
        }}
        open={loading}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          {t('loading.generating')}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
          {t('loading.exportSub')}
        </Typography>
      </Backdrop>
    </Box>
  );
};

export default InformePartidoPageModern;
